import {Injectable} from '@angular/core';
import {ShellClientService} from '../../shell-communication/shell-client.service';
import {GetFairValueShell, GetFairValueShellResponse} from '../../shell-communication/shell-operations-protocol';
import {LastQuoteCacheService} from '../../last-quote-cache.service';
import {isValidNumber} from '../../utils';

const SPX_STRIKE_INCREMENT = 5;

@Injectable()
export class EsFairValueService {

    constructor(
        private readonly _shellClient: ShellClientService,
        private readonly _lastQuoteCache: LastQuoteCacheService
    ) {
    }

    private readonly _cache = new Map<string, number>();

    async getFairValue(esContractTicker: string, date?: Date): Promise<number> {

        const effectiveDate = date || new Date();
        const dateIso = effectiveDate.toISOString().substring(0, 10);
        const cacheKey = `${esContractTicker}|${dateIso}`;

        if (this._cache.has(cacheKey)) {
            return this._cache.get(cacheKey);
        }

        let rawValue: number | null = null;

        try {
            const query = new GetFairValueShell(esContractTicker, dateIso);
            const response = await this._shellClient.processQuery<GetFairValueShellResponse>(query);

            console.log('EsFairValue response:', response);

            if (response.isSuccess && isValidNumber(response.fairValue, true)) {
                rawValue = response.fairValue;
            } else {
                console.warn('EsFairValue: backend returned error or null value, using live price fallback',
                    response.error || 'null fairValue');
            }
        } catch (e) {
            console.warn('EsFairValue: shell query failed, using live price fallback', e);
        }

        if (!isValidNumber(rawValue, true)) {
            rawValue = this.computeFallbackFromLivePrices(esContractTicker);
        }

        const rounded = this.roundToStrikeIncrement(rawValue);

        console.log(`EsFairValue rounded: ${rawValue} → ${rounded}`);

        this._cache.set(cacheKey, rounded);

        return rounded;
    }

    private computeFallbackFromLivePrices(esContractTicker: string): number {
        const esQuote = this._lastQuoteCache.getLastQuote(esContractTicker);
        const spxQuote = this._lastQuoteCache.getLastQuote('SPX');

        const esPrice = esQuote?.lastPx;
        const spxPrice = spxQuote?.lastPx;

        if (isValidNumber(esPrice, true) && isValidNumber(spxPrice, true)) {
            const fallback = Math.abs(esPrice - spxPrice);
            console.log(`EsFairValue fallback from live prices: |${esPrice} - ${spxPrice}| = ${fallback}`);
            return fallback;
        }

        console.warn('EsFairValue: live prices unavailable, returning 0');
        return 0;
    }

    private roundToStrikeIncrement(value: number): number {
        return Math.round(value / SPX_STRIKE_INCREMENT) * SPX_STRIKE_INCREMENT;
    }
}
